import React from 'react';
import { Link } from 'react-router-dom';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import '../projects/ProjectPage.css';
import './ResearchDetailPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2012-09-27', displayDate: 'September 27, 2012' };

const AdvancedMatlabBoxplotPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  return (
    <div className="project-page research-detail-page" id="top">
      <div className="box" style={{ marginBottom: 'var(--spacing-md)' }}>Beta</div>
      <h1 className="title">Advanced Box Plot for Matlab</h1>

      <h2>Overview</h2>
      <p>
        To illustrate the statistical properties of large data sets, Matlab includes a builtin box plot function.
        Although this function is a suitable tool in everyday use, some circumstances may require a more powerful
        alternative. These include the addition of the sample mean to the boxplot data, the grouping of data based on a
        given criterion and customized coloring for publishing. To address these requirements, in this page, we introduce
        an advanced box plot function designed for Matlab power users.
      </p>

      <h2>Download</h2>
      <ul>
        <li><a href="/download/aboxplot.zip">aboxplot.zip</a> (ZIP/3.48 KB)</li>
      </ul>
      <p>Beta 1 version as of September 27, 2012.</p>

      <h2>Usage</h2>
      <p>To plot an advanced boxplot, call the following function:</p>
      <div className="box-code"><pre>aboxplot(x);</pre></div>
      <p>or:</p>
      <div className="box-code"><pre>aboxplot(x, 'argument_name', argument_value, ...);</pre></div>
      <p>where <em>x</em> may be:</p>
      <ul>
        <li>A two-dimensional <em>n</em> × <em>m</em> numeric matrix. The figure will contain <em>m</em> box plot bars.</li>
        <li>A three-dimensional <em>p</em> × <em>n</em> × <em>m</em> numeric array. The figure will contain <em>m</em> groups of <em>p</em> box plot bars each.</li>
        <li>An array of <em>p</em> cells, where each cell contains a <em>n</em> × <em>m</em> numeric matrix.</li>
      </ul>

      <h2>Optional Arguments</h2>
      <table className="information">
        <tbody>
          <tr><th className="strong" style={{ width: '150px' }}>Argument name</th><th>Argument description</th></tr>
          <tr><td className="strong">'Colorgrad'</td><td>Color gradient: blue_down, blue_up, green_down, green_up, orange_down, orange_up, red_down, red_up</td></tr>
          <tr><td className="strong">'Colorrev'</td><td>If true, color gradient applies across groups instead of within a group.</td></tr>
          <tr><td className="strong">'Colormap'</td><td>Custom colors for the box plot bars.</td></tr>
          <tr><td className="strong">'Labels'</td><td>The X-axis labels, corresponding to the <em>m</em> groups.</td></tr>
          <tr><td className="strong">'OutlierMarker'</td><td>The marker used to plot the outliers.</td></tr>
          <tr><td className="strong">'WidthE', 'WidthL', 'WidthS'</td><td>Width parameters for whisker edge, group, and bar.</td></tr>
        </tbody>
      </table>

      <h2>Tutorial</h2>
      <h3>Single Box Plot</h3>
      <p>This example plots three data sets of normally distributed random variables with means 5, 10 and 15.</p>
      <img src="/research/advanced_matlab_boxplot/FigureSingleSmall.png" alt="Single box plot" width={420} height={320} style={{ maxWidth: '100%' }} />
      <p className="figure">Single box plot</p>

      <h3>Grouped Box Plot</h3>
      <p>Grouped data can be plotted using a 3D array or a cell array.</p>
      <img src="/research/advanced_matlab_boxplot/FigureMultipleArraySmall.png" alt="Grouped box plot" width={420} height={320} style={{ maxWidth: '100%' }} />
      <p className="figure">Grouped box plot with 3D array</p>

      <h2>Version History</h2>
      <table className="information">
        <tbody>
          <tr><th style={{ width: '150px' }}>Version</th><th className="strong" style={{ width: '150px' }}>Date</th><th>Description</th></tr>
          <tr><td>Beta 1</td><td className="strong">September 27, 2012</td><td>Initial beta release.</td></tr>
        </tbody>
      </table>

      <p style={{ marginTop: 'var(--spacing-lg)' }}>
        <Link to="/research">← Research</Link>
      </p>
      <p className="update">Published: September 27, 2012. Last updated: September 27, 2012</p>
    </div>
  );
};

export default AdvancedMatlabBoxplotPage;
